import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { 
  ParceiroMetrics, 
  ParceiroDetailedMetrics, 
  ParceiroLead, 
  ParceiroFilters,
  Estado,
  Cidade,
  CadastrarParceiroData,
  CadastrarParceiroResponse,
  AtualizarParceiroData,
  AtualizarParceiroResponse,
  Vendedor,
  TransferirParceiroData,
  TransferirParceiroResponse
} from '@/types/parceiros';

/**
 * Serviço para gerenciamento de parceiros
 * Todas as requisições são autenticadas via fetchWithAuth
 */
export class ParceirosService {
  
  /**
   * Busca lista de estados brasileiros
   */
  static async getEstados(): Promise<Estado[]> {
    try {
      const response = await fetchWithAuth('/api/parceiros/estados');
      
      if (!response.ok) {
        throw new Error('Erro ao buscar estados');
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao buscar estados:', error);
      throw error;
    }
  }
  
  /**
   * Busca lista de cidades de um estado específico
   * @param uf Sigla do estado (ex: SP, RJ, MG)
   */
  static async getCidades(uf: string): Promise<Cidade[]> {
    try {
      if (!uf || uf.length !== 2) {
        throw new Error('UF inválida');
      }
      
      const response = await fetchWithAuth(`/api/parceiros/cidades/${uf.toUpperCase()}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao buscar cidades');
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      throw error;
    }
  }
  
  /**
   * Busca lista de parceiros com métricas
   * Vendedor vê apenas seus parceiros, admin vê todos
   */
  static async getParceiros(filters?: ParceiroFilters): Promise<ParceiroMetrics[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.periodo) params.append('periodo', filters.periodo);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.ordenacao) params.append('ordenacao', filters.ordenacao);
      if (filters?.search) params.append('search', filters.search);
      
      const queryString = params.toString();
      const url = `/api/parceiros${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetchWithAuth(url);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao buscar parceiros');
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao buscar parceiros:', error);
      throw error;
    }
  }
  
  /**
   * Cadastra novo parceiro
   * Valida cidade e estado obrigatórios
   */
  static async cadastrarParceiro(data: CadastrarParceiroData): Promise<CadastrarParceiroResponse> {
    try {
      const response = await fetchWithAuth('/api/parceiros', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao cadastrar parceiro');
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao cadastrar parceiro:', error);
      throw error;
    }
  }
  
  /**
   * Busca métricas detalhadas de um parceiro
   * Inclui histórico mensal dos últimos 12 meses
   */
  static async getParceiroDetails(parceiroId: string): Promise<ParceiroDetailedMetrics> {
    try {
      const response = await fetchWithAuth(`/api/parceiros/${parceiroId}/metrics`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao buscar detalhes do parceiro');
      }
      
      const data = await response.json();
      
      // Transformar estrutura do backend para o formato esperado pelo tipo
      // Backend retorna: { parceiro: {...}, metrics: [...] }
      // Tipo espera: estrutura flat com parceiro_* + metrics
      
      // Calcular métricas agregadas dos leads do parceiro
      const metricsAgregadas = data.metrics.reduce(
        (acc: any, m: any) => ({
          leads: acc.leads + m.leads,
          propostas: acc.propostas + m.propostas,
          contratos: acc.contratos + m.contratos,
          valor_propostas: acc.valor_propostas + m.valor_propostas,
          valor_contratos: acc.valor_contratos + m.valor_contratos
        }),
        { leads: 0, propostas: 0, contratos: 0, valor_propostas: 0, valor_contratos: 0 }
      );
      
      // Calcular métricas dos últimos 30 dias
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const metrics30Dias = data.metrics
        .filter((m: any) => new Date(m.mes) >= thirtyDaysAgo)
        .reduce(
          (acc: any, m: any) => ({
            leads: acc.leads + m.leads,
            propostas: acc.propostas + m.propostas,
            contratos: acc.contratos + m.contratos,
            valor_propostas: acc.valor_propostas + m.valor_propostas,
            valor_contratos: acc.valor_contratos + m.valor_contratos
          }),
          { leads: 0, propostas: 0, contratos: 0, valor_propostas: 0, valor_contratos: 0 }
        );
      
      const metrics7Dias = data.metrics
        .filter((m: any) => new Date(m.mes) >= sevenDaysAgo)
        .reduce((acc: any, m: any) => acc + m.leads, 0);
      
      const totalLeads = metricsAgregadas.leads;
      const totalContratos = metricsAgregadas.contratos;
      const totalPropostas = metricsAgregadas.propostas;
      
      return {
        parceiro_id: data.parceiro.id,
        parceiro_nome: data.parceiro.nome,
        parceiro_telefone: data.parceiro.telefone,
        parceiro_email: data.parceiro.email,
        parceiro_cidade: data.parceiro.cidade,
        parceiro_estado: data.parceiro.estado,
        parceiro_cpf: data.parceiro.cpf,
        parceiro_pix: data.parceiro.pix,
        data_inicio: data.parceiro.created_at,
        data_aprovacao: undefined,
        status: data.parceiro.ativo,
        
        vendedor_id: data.parceiro.vendedor_id,
        vendedor_nome: undefined,
        
        // Métricas de leads
        total_leads: totalLeads,
        leads_30_dias: metrics30Dias.leads,
        leads_7_dias: metrics7Dias,
        
        // Métricas de propostas
        propostas_enviadas: totalPropostas,
        propostas_30_dias: metrics30Dias.propostas,
        valor_propostas: metricsAgregadas.valor_propostas,
        valor_propostas_30_dias: metrics30Dias.valor_propostas,
        
        // Métricas de contratos
        contratos_fechados: totalContratos,
        contratos_30_dias: metrics30Dias.contratos,
        valor_contratos: metricsAgregadas.valor_contratos,
        valor_contratos_30_dias: metrics30Dias.valor_contratos,
        
        // Taxas de conversão
        taxa_conversao_leads_contratos: totalLeads > 0 
          ? Number(((totalContratos / totalLeads) * 100).toFixed(2))
          : 0,
        taxa_conversao_leads_propostas: totalLeads > 0 
          ? Number(((totalPropostas / totalLeads) * 100).toFixed(2))
          : 0,
        
        // Array de métricas
        metrics: data.metrics
      };
    } catch (error) {
      console.error('Erro ao buscar detalhes do parceiro:', error);
      throw error;
    }
  }
  
  /**
   * Busca leads de um parceiro específico
   * Suporta filtros por período, status e busca
   */
  static async getParceiroLeads(
    parceiroId: string, 
    filters?: {
      periodo?: string;
      status?: string;
      search?: string;
    }
  ): Promise<ParceiroLead[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.periodo) params.append('periodo', filters.periodo);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.search) params.append('search', filters.search);
      
      const queryString = params.toString();
      const url = `/api/parceiros/${parceiroId}/leads${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetchWithAuth(url);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao buscar leads do parceiro');
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao buscar leads do parceiro:', error);
      throw error;
    }
  }

  /**
   * Atualiza dados de um parceiro
   * Admin pode editar qualquer parceiro
   * Vendedor só pode editar seus próprios parceiros
   */
  static async updateParceiro(id: string, data: AtualizarParceiroData): Promise<AtualizarParceiroResponse> {
    try {
      const response = await fetchWithAuth(`/api/parceiros/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        
        switch (response.status) {
          case 400:
            throw new Error(error.error || 'Dados inválidos');
          case 403:
            throw new Error('Sem permissão para editar este parceiro');
          case 404:
            throw new Error('Parceiro não encontrado');
          default:
            throw new Error(error.error || 'Erro ao atualizar parceiro');
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao atualizar parceiro:', error);
      throw error;
    }
  }

  /**
   * Busca lista de vendedores disponíveis
   * Apenas para administradores
   */
  static async getVendedores(): Promise<Vendedor[]> {
    try {
      const response = await fetchWithAuth('/api/parceiros/vendedores');
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao buscar vendedores');
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao buscar vendedores:', error);
      throw error;
    }
  }

  /**
   * Transfere um parceiro para outro vendedor
   * Atualiza o parceiro e todos os seus leads
   * Apenas para administradores
   */
  static async transferirParceiro(
    parceiroId: string, 
    data: TransferirParceiroData
  ): Promise<TransferirParceiroResponse> {
    try {
      const response = await fetchWithAuth(`/api/parceiros/${parceiroId}/vendedor`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao transferir parceiro');
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao transferir parceiro:', error);
      throw error;
    }
  }
}

// Exportar instância para uso direto
export const parceirosService = ParceirosService;


